// The review record — constructors, labels and the small amount of logic that
// is about a review rather than about storage.
//
// The shape of the header block is not ours to invent. It is the FEMP ESPC
// Project Review Comment Template, Process Doc P3-09 rev 09/2025, field for
// field, in the order the form prints them. See docs/redesign/P3-09.md.

import type {
  Disposition,
  DismissReason,
  HeaderFields,
  MutePrompts,
  MutedCheck,
  MutedChecks,
  PaneState,
  ReviewRecord,
  SubmittalType,
} from '../types';

/** "Type of Submittal/Deliverable", as the form lists them. */
export const SUBMITTAL_TYPES: { value: SubmittalType; label: string }[] = [
  { value: 'preliminary-assessment', label: 'Preliminary Assessment' },
  {
    value: 'iga-midpoint-review',
    label: 'Proposal/Investment Grade Audit (IGA) Midpoint Review',
  },
  { value: 'proposal-iga', label: 'Proposal/IGA' },
  { value: 'commissioning-plan', label: 'Commissioning Plan' },
  { value: 'commissioning-report', label: 'Commissioning Report' },
  { value: 'post-installation-report', label: 'Post-Installation Report' },
  { value: 'year-1-annual-report', label: 'Year 1 Annual Report' },
  { value: 'other', label: 'Other (ID)' },
];

export function submittalLabel(value: SubmittalType): string {
  return SUBMITTAL_TYPES.find((t) => t.value === value)?.label ?? value;
}

/**
 * Why a finding was set aside. Shown as the choices on "not a concern", and
 * written onto the record so the decision can be explained later.
 *
 * `label` is what the reviewer picks from. `short` is the same reason inside
 * the one line a decided finding shows — "Not a concern — standard practice" —
 * where the full sentence would not fit and does not need to. `because` is the
 * clause that follows the word "because" in the check-tuning panel, which is a
 * sentence rather than a label and cannot use either of the other two without
 * reading as broken English.
 */
export const DISMISS_REASONS: {
  value: DismissReason;
  label: string;
  short: string;
  because: string;
}[] = [
  {
    value: 'addressed-elsewhere',
    label: 'Already addressed elsewhere in the plan',
    short: 'addressed elsewhere',
    because: 'it is already addressed elsewhere in the plan',
  },
  {
    value: 'standard-practice',
    label: 'This is standard practice for this kind of project',
    short: 'standard practice',
    because: 'it is standard practice for this kind of project',
  },
  {
    value: 'not-applicable',
    label: 'Doesn’t apply to this project type',
    short: 'doesn’t apply to this project type',
    because: 'it doesn’t apply to this project type',
  },
  {
    value: 'misread',
    label: 'Ceiling misread the document',
    short: 'Ceiling misread the document',
    because: 'Ceiling misread the document',
  },
  {
    value: 'not-worth-a-comment',
    label: 'Real, but not worth a comment',
    short: 'not worth a comment',
    because: 'it is real, but not worth a comment',
  },
  {
    value: 'other',
    label: 'Something else',
    short: 'another reason',
    because: 'of something else',
  },
];

function dismissReasonShort(value: DismissReason): string {
  return DISMISS_REASONS.find((r) => r.value === value)?.short ?? value;
}

/** The clause that follows "because" in the check-tuning panel's sentence. */
export function dismissReasonBecause(value: DismissReason): string {
  return DISMISS_REASONS.find((r) => r.value === value)?.because ?? value;
}

/**
 * The one line a decided finding shows, after "Decided:".
 *
 * Reads back what the reviewer chose, in their own words rather than the
 * stored value: "Not a concern — standard practice", "Asked an SME — Ruiz,
 * mechanical". A field that was left empty is left out rather than rendered as
 * a dash with nothing after it.
 */
export function describeDisposition(disposition: Disposition): string {
  switch (disposition.decision) {
    case 'include':
      return 'Included as a comment';
    case 'not-a-concern': {
      const why =
        disposition.reason === 'other'
          ? disposition.reasonText?.trim()
          : disposition.reason
            ? dismissReasonShort(disposition.reason)
            : undefined;
      return why ? `Not a concern — ${why}` : 'Not a concern';
    }
    case 'refer': {
      const who = disposition.referredTo?.trim();
      return who ? `Asked an SME — ${who}` : 'Asked an SME';
    }
    default:
      return 'Decided';
  }
}

/** P3-09 defaults to the Proposal/IGA submittal, which is the common case. */
export function emptyHeaderFields(): HeaderFields {
  return {
    projectName: '',
    contractNo: '',
    documentDate: '',
    commentsDueBy: '',
    submittalType: 'proposal-iga',
    submittalOther: '',
    reviewerName: '',
    reviewerDate: '',
    reviewerPhone: '',
    reviewerEmail: '',
  };
}

function emptyPaneState(): PaneState {
  return {
    selectedFindingId: null,
    documentPage: null,
    evidenceFilter: 'all',
    decisionFilter: 'all',
  };
}

export function newReviewRecord(
  docId: string,
  fileName: string,
  now = new Date().toISOString(),
): ReviewRecord {
  return {
    docId,
    fileName,
    headerFields: emptyHeaderFields(),
    dispositions: {},
    editedComments: {},
    skippedChecks: [],
    paneState: emptyPaneState(),
    createdAt: now,
    updatedAt: now,
  };
}

/**
 * A record read back from storage may have been written by an older build.
 * Filling the gaps beats refusing to open a review the reviewer has already
 * done work in.
 */
export function normaliseReviewRecord(
  record: ReviewRecord,
  docId: string,
): ReviewRecord {
  return {
    ...record,
    docId: record.docId || docId,
    fileName: record.fileName ?? '',
    headerFields: { ...emptyHeaderFields(), ...(record.headerFields ?? {}) },
    dispositions: record.dispositions ?? {},
    editedComments: record.editedComments ?? {},
    skippedChecks: record.skippedChecks ?? [],
    paneState: { ...emptyPaneState(), ...(record.paneState ?? {}) },
    createdAt: record.createdAt ?? record.updatedAt ?? '',
    updatedAt: record.updatedAt ?? record.createdAt ?? '',
  };
}

/**
 * "Table 1-1 / Table 4-4 · p6" — as much of it as the finding actually has.
 *
 * A finding with no section reference and no page gets no chip at all rather
 * than an empty one, and a missing page never leaves a trailing separator.
 * It is here rather than in the component that shows it because the comment
 * sheet's location column is the same string, and the two must not drift.
 */
export function locationChip(
  sectionRef: string | null | undefined,
  page: number | null | undefined,
): string | null {
  const parts = [
    sectionRef?.trim() || null,
    typeof page === 'number' && Number.isFinite(page) ? `p${page}` : null,
  ].filter((p): p is string => p !== null);
  return parts.length > 0 ? parts.join(' · ') : null;
}

/** The comment that would go on the sheet: the reviewer's edit, or the draft. */
export function commentFor(
  review: Pick<ReviewRecord, 'editedComments'>,
  findingId: string,
  draft: string,
): string {
  const edited = review.editedComments[findingId];
  return edited !== undefined ? edited : draft;
}

/**
 * How many times running the same reason has to come up before a check is
 * offered as mutable. Three is enough to be a pattern and few enough that a
 * reviewer meets it inside one plan.
 */
export const MUTE_THRESHOLD = 3;

/**
 * A check the reviewer could be offered the chance to quiet, and the sentence
 * the offer is made in. Never a decision: the panel that shows this is
 * dismissible and muting is always the reviewer's own act.
 */
export interface MuteCandidate {
  failureModeId: string;
  reason: DismissReason;
  count: number;
}

/**
 * How often each check has been set aside, and for which reason, across every
 * review in this browser. Keyed by `mutePromptKey()`.
 *
 * Across, not within: a reviewer who sets the same check aside once in each of
 * three plans has told us the same thing as one who did it three times in one,
 * and BRIEF.md's third loop is explicitly the across-documents one. The tally
 * is derived from the stored reviews rather than kept as a running count, so it
 * cannot drift from them and a deleted review takes its dismissals with it —
 * which is what "deleting removes every trace" has to mean.
 */
export type DismissalTally = Record<string, number>;

/**
 * Adds one review's dismissals to a tally. `modeOf` answers which check a
 * finding came from; a finding whose record is no longer in this browser
 * answers undefined and is not counted, because there is nothing to attribute
 * it to.
 */
export function addDismissals(
  tally: DismissalTally,
  dispositions: Record<string, Disposition>,
  modeOf: (findingId: string) => string | undefined,
): DismissalTally {
  const next: DismissalTally = { ...tally };
  for (const d of Object.values(dispositions)) {
    if (d.decision !== 'not-a-concern' || !d.reason) continue;
    const mode = modeOf(d.findingId);
    if (!mode) continue;
    const key = mutePromptKey(mode, d.reason);
    next[key] = (next[key] ?? 0) + 1;
  }
  return next;
}

/**
 * Which check, if any, the reviewer has now set aside often enough for the
 * same reason that continuing to raise it is noise.
 *
 * Muting hides findings; it never stops a check running, and it is never
 * automatic — this returns a candidate for the UI to offer, not a decision.
 */
export function muteCandidate(
  tally: DismissalTally,
  muted: MutedChecks,
  declined: MutePrompts = {},
): MuteCandidate | null {
  // Deterministic: the same tally must always produce the same offer, or the
  // panel would change identity between two renders of the same state.
  for (const key of Object.keys(tally).sort()) {
    const count = tally[key];
    if (count < MUTE_THRESHOLD) continue;
    const [failureModeId, reason] = key.split('::');
    if (muted[failureModeId as keyof MutedChecks]) continue;
    // "Keep showing it" is an answer, not a postponement. The offer comes back
    // only if the same reason comes up again after it — the reviewer's position
    // may have changed by the fifth time, but they should not be asked twice
    // about the same three.
    const declinedAt = declined[key];
    if (declinedAt !== undefined && count <= declinedAt) continue;
    return { failureModeId, reason: reason as DismissReason, count };
  }
  return null;
}

/**
 * How a dismissal is keyed: by the check *and* the reason. A reviewer who keeps
 * FM-09 because it was misread has said nothing about keeping it when it turns
 * out not to apply to the project type.
 */
export function mutePromptKey(
  failureModeId: string,
  reason: DismissReason,
): string {
  return `${failureModeId}::${reason}`;
}

export function mutedCheck(
  failureModeId: MutedCheck['failureModeId'],
  reason: DismissReason,
  count: number,
  now = new Date().toISOString(),
): MutedCheck {
  return { failureModeId, reason, count, mutedAt: now, source: 'dismissals' };
}

/**
 * The same thing, chosen on /checks rather than offered after three
 * dismissals. There is no recurring reason behind it and no count, so it says
 * so rather than borrowing either: the reason is recorded as the reviewer's
 * own choice and the count is zero, which is the truth about how many times
 * they set a finding aside before deciding they did not want the check.
 */
export function mutedFromReference(
  failureModeId: MutedCheck['failureModeId'],
  now = new Date().toISOString(),
): MutedCheck {
  return {
    failureModeId,
    reason: 'not-applicable',
    count: 0,
    mutedAt: now,
    source: 'reference',
  };
}

/* ==========================================================================
   The comment sheet
   --------------------------------------------------------------------------
   The FEMP ESPC Project Review Comment Template's table, column for column and
   in the order the form prints them. See docs/redesign/P3-09.md.

   The last two columns are deliberately empty. They belong to the contractor
   and to the agency, and their presence on the sheet is part of what makes the
   export recognisable as the real form.
   ========================================================================== */

export const COMMENT_COLUMNS = [
  'No.',
  'Page#, ECM, Sect., or Dwg.',
  'Reviewer Comment',
  'Reviewer',
  'Contractor Response',
  'Agency Response (Accept or Additional Discussion)',
] as const;

export interface CommentRow {
  /** The finding this row came from, so a row can be traced back. */
  findingId: string;
  no: number;
  location: string;
  comment: string;
  reviewer: string;
}

/**
 * How the sheet is built from a review — the numbering, the filtering, the
 * header block and the text form — lives in src/report/commentSheet.ts,
 * beside the .docx and .xlsx writers that render the same structure. What
 * stays here is what the shape of a row is, and the column order the form
 * prints, because those are facts about the review rather than about an
 * export.
 */
